import Select from "react-select";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import useVideoBulkUpload from "./useVideoBulkUpload";
import Style from "../../Podcast/AudioBulkUpload/audioBulkUpload.module.scss";

const formatSize = (bytes) => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const STATUS_LABEL = {
  pending: "Ready",
  uploaded: "Uploaded",
  failed: "Upload failed",
  not_created: "Not created",
};

const VideoBulkUpload = ({
  endpoint,
  entityLabel = "video",
  requireCategory = false,
  refetch,
  handleClose,
}) => {
  const {
    fileInputRef,
    items,
    isUploading,
    categories,
    category,
    categoryError,
    createError,
    handleCategoryChange,
    acceptTypes,
    handleFileChange,
    handleRemove,
    handleUpload,
    handleCancel,
  } = useVideoBulkUpload({ endpoint, entityLabel, requireCategory, refetch, handleClose });

  const toUploadCount = items.filter(
    (item) => item.status === "pending" || item.status === "failed"
  ).length;
  const notCreatedCount = items.filter((item) => item.status === "not_created").length;
  const actionCount = toUploadCount + notCreatedCount;
  // nothing left to send to Vimeo means the button only creates the missing entries
  const actionLabel = toUploadCount === 0 && notCreatedCount > 0 ? "Retry" : "Upload";

  return (
    <div>
      <p className="pro-font-sm pro-mb-4">
        Select the video files. Each file is uploaded to Vimeo and created as a separate{" "}
        {entityLabel} with the file name as its title. Entries are added as inactive with no
        other details; add them from the list once the video has been processed.
      </p>

      {requireCategory && (
        <div className="input-wrap pro-mb-4">
          <label htmlFor="video_bulk_category" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Category*
          </label>
          <Select
            id="video_bulk_category"
            placeholder={"Select"}
            className={`pro-input lg ${categoryError ? "error" : ""}`}
            classNamePrefix="pro-input"
            options={categories}
            getOptionValue={(option) => option?._id}
            getOptionLabel={(option) => option?.name}
            value={category}
            onChange={handleCategoryChange}
            isDisabled={isUploading}
          />
          {categoryError && <span className="error-text">{categoryError}</span>}
        </div>
      )}

      <div className="input-wrap pro-mb-4">
        <label
          htmlFor="video_bulk_files"
          className={`${Style.drop_zone} ${isUploading ? Style.disabled : ""}`}
        >
          <span className="material-symbols-outlined">video_file</span>
          <span className="pro-fw-medium">Click to select video files</span>
          <span className="pro-font-xs pro-opacity-60">
            MP4, OGX, OGA, OGV, OGG, WEBM, MKV or MOV, up to 20 files
          </span>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            accept={acceptTypes}
            id="video_bulk_files"
            className={Style.file_input}
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </label>
      </div>

      {items.length > 0 && (
        <div className={`pro-mb-4 ${Style.list_wrap}`}>
          <table className={Style.list}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Size</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <p className={`pro-mb-0 pro-fw-medium ${Style.title}`} title={item.title}>
                      {item.title}
                    </p>
                    <p
                      className={`pro-mb-0 pro-font-xs pro-opacity-60 ${Style.title}`}
                      title={item.name}
                    >
                      {item.name}
                    </p>
                  </td>
                  <td className="pro-font-sm pro-nowrap">{formatSize(item.size)}</td>
                  <td>
                    {item.status === "uploading" ? (
                      <div className={Style.progress}>
                        <span style={{ width: `${item.progress}%` }} />
                        <em>{item.progress}%</em>
                      </div>
                    ) : (
                      <span
                        className={`pro-font-xs ${
                          item.status === "not_created" || item.status === "failed"
                            ? Style.status_warn
                            : ""
                        }`}
                      >
                        {STATUS_LABEL[item.status]}
                      </span>
                    )}
                  </td>
                  <td>
                    {!isUploading && item.status !== "uploaded" && (
                      <button
                        type="button"
                        className="pro-btn-link pro-p-0"
                        onClick={() => handleRemove(item.id)}
                        aria-label={`Remove ${item.name}`}
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createError && (
        <div className={`pro-mb-4 ${Style.create_error}`} role="alert">
          <p className="pro-mb-1 pro-fw-medium">{createError}</p>
          <p className="pro-mb-0 pro-font-xs">
            {notCreatedCount} file(s) are on Vimeo but no {entityLabel} was created for them.
            Press Retry to create the entries; the files are not uploaded to Vimeo again.
          </p>
        </div>
      )}

      <div className="col-12 pro-mt-4 pro-d-flex pro-justify-end">
        <Button className={"pro-btn-link lg pro-px-5"} onClick={handleCancel} type="button">
          Cancel
        </Button>
        <Button
          className={"pro-btn-primary lg pro-mx-3"}
          type="button"
          onClick={handleUpload}
          disabled={isUploading || actionCount === 0}
        >
          {isUploading
            ? "Uploading..."
            : `${actionLabel}${actionCount ? ` (${actionCount})` : ""}`}
        </Button>
      </div>
    </div>
  );
};

export default VideoBulkUpload;
